/**
 * Selectors for shop-scoped Redux state (config + shop details).
 *
 * Reads come from the per-shop maps in `allOrderManagement.configByShopId`
 * and `allOrderManagement.shopDetailsByShopId`. Prefer these selectors over
 * touching state directly so the storage shape can evolve later.
 */

import type { AllOrderManagementState } from "./allOrders.slice";
import type {
  AsyncEntry,
  ShopConfig,
  ShopDetails,
} from "../types/shopConfig";

// Loose root shape — the app doesn't have a single canonical RootState import
// used everywhere, so we accept anything that carries the slice we need.
type RootStateLike = {
  allOrderManagement: AllOrderManagementState;
};

export const selectShopId = (state: RootStateLike): string | null =>
  state.allOrderManagement?.shopId ?? null;

export const selectShopConfigEntry = (
  state: RootStateLike,
  shopId: string | null,
): AsyncEntry<ShopConfig> | undefined => {
  if (!shopId) return undefined;
  return state.allOrderManagement?.configByShopId?.[shopId];
};

export const selectShopDetailsEntry = (
  state: RootStateLike,
  shopId: string | null,
): AsyncEntry<ShopDetails> | undefined => {
  if (!shopId) return undefined;
  return state.allOrderManagement?.shopDetailsByShopId?.[shopId];
};

export const selectShopConfig = (
  state: RootStateLike,
  shopId: string | null,
): ShopConfig | null =>
  selectShopConfigEntry(state, shopId)?.data ?? null;

export const selectShopDetails = (
  state: RootStateLike,
  shopId: string | null,
): ShopDetails | null =>
  selectShopDetailsEntry(state, shopId)?.data ?? null;

export const selectCurrentShopConfig = (
  state: RootStateLike,
): ShopConfig | null => selectShopConfig(state, selectShopId(state));

export const selectCurrentShopDetails = (
  state: RootStateLike,
): ShopDetails | null => selectShopDetails(state, selectShopId(state));

export const selectCurrentShopConfigEntry = (
  state: RootStateLike,
): AsyncEntry<ShopConfig> | undefined =>
  selectShopConfigEntry(state, selectShopId(state));

export const selectCurrentShopDetailsEntry = (
  state: RootStateLike,
): AsyncEntry<ShopDetails> | undefined =>
  selectShopDetailsEntry(state, selectShopId(state));
